/**
 * fullTimersData.js — shared data loader for the Finance "Full Timers"
 * feature (2026-07-11, batch-B G1; upgraded by G7 the same day).
 *
 * G1 shipped this as an O(N) fan-out (one GET .../finance/player/:userId
 * call PER full-time player, because payments.js was out of scope for
 * that batch). G7 adds a real backend aggregate — GET /payments/team/
 * :teamId/finance/season-fees (backend/src/routes/payments.js) — so this
 * file now does exactly ONE request. `loadSeasonFeeSummary` is the new,
 * richer loader (per-player status + lastPaymentAt + team-level summary
 * + the admin-configured processing-fee estimate) that FullTimersTab.jsx
 * uses directly.
 *
 * `loadFullTimersData` is KEPT for backward compatibility — OverviewTab.jsx
 * still calls it and expects the G1 shape ({ players: [{userId, name,
 * email, paidSeasonFeeCents}], totalCollectedCents, currency }) for its
 * "Collected from Full Timers" card. Rather than touch OverviewTab.jsx
 * (small risk, no functional need), this function now just calls the new
 * aggregate once and re-shapes the response to match the old contract.
 * The `membersOverride` parameter is no longer used (the aggregate computes
 * the full-time roster server-side) but the parameter is kept so existing
 * call sites don't need to change.
 */
import api from '../../services/api.js';

/** GET /teams/:teamId/members — returns the raw array (roster + pending invites). */
export async function fetchMembers(teamId) {
  const { data } = await api.get(`/teams/${teamId}/members`);
  return Array.isArray(data) ? data : [];
}

// Full-time = is_primary true, a real (non-placeholder, non-deleted) user.
// Placeholders (userId null) have no account to charge/track, so they're
// excluded — a coach collecting a placeholder's season fee has to do it
// out-of-band until that person actually creates an account. Exported so
// OverviewTab can use the SAME predicate to exclude full-timers' season-fee
// dollars from the Spares total (see the double-count note there).
export function filterFullTimeMembers(members) {
  return (members || []).filter(m => m.isPrimary === true && m.userId && !m.isDeleted);
}

/**
 * GET /payments/team/:teamId/finance/season-fees — the G7 backend
 * aggregate. Returns everything FullTimersTab needs in one request:
 * the configured fee, an admin-config-driven processing-fee estimate
 * (labeled an estimate in the UI — the real charge is computed fresh
 * at checkout), and per-player paid/status/lastPaymentAt.
 *
 * @param {string} teamId
 * @returns {Promise<{
 *   feeCents:number, currency:string, passToPlayer:boolean,
 *   estimatedProcessingCents:number, estimatedPlayerTotalCents:number,
 *   players: Array<{userId,name,email,paidCents,status,lastPaymentAt}>,
 *   summary: {count,paidCount,outstandingCount,collectedCents,outstandingCents}
 * }>}
 */
export async function loadSeasonFeeSummary(teamId) {
  const { data } = await api.get(`/payments/team/${teamId}/finance/season-fees`);
  return data;
}

/**
 * Back-compat wrapper for OverviewTab.jsx — see file header. Re-shapes
 * the G7 aggregate into the original G1 per-player shape.
 *
 * @param {string} teamId
 * @param {Array} [_membersOverride] — unused since G7 (kept for signature compat).
 * @returns {Promise<{ players: Array<{userId, name, email, paidSeasonFeeCents}>, totalCollectedCents: number, currency: string }>}
 */
export async function loadFullTimersData(teamId, _membersOverride) {
  const agg = await loadSeasonFeeSummary(teamId);
  return {
    players: (agg.players || []).map(p => ({
      userId: p.userId, name: p.name, email: p.email || '', paidSeasonFeeCents: p.paidCents,
    })),
    totalCollectedCents: agg.summary?.collectedCents || 0,
    currency: agg.currency || 'cad',
  };
}
